import { useEffect, useState } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { Button, Text } from "@andrum/ui";
import { useExperimentStore } from "@/stores/experiment-store";
import { MoodSelector } from "@/features/check-in/components/MoodSelector";
import { hapticService } from "@/lib/capacitor";
import { springs, durations } from "@/lib/motion/tokens";
import { cn } from "@/lib/utils";
import type { Mood } from "@andrum/schemas";
import {
  advanceProgramDay,
  getActiveEnrollment,
  type Enrollment,
} from "@/features/programs/api/programs-api";

export function ReflectScreen() {
  const { t } = useTranslation();
  const { assignmentId } = useParams({ strict: false }) as {
    assignmentId: string;
  };
  const navigate = useNavigate();
  const { completeAssignment, getExperimentForAssignment } =
    useExperimentStore();

  const experiment = getExperimentForAssignment(assignmentId);

  const [didIt, setDidIt] = useState<boolean | null>(null);
  const [afterMood, setAfterMood] = useState<Mood | null>(null);
  const [repeatIntent, setRepeatIntent] = useState<0 | 1 | 2 | null>(null);
  const [note, setNote] = useState("");
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [advancing, setAdvancing] = useState(false);
  const [advanceNote, setAdvanceNote] = useState<string | null>(null);

  useEffect(() => {
    void getActiveEnrollment().then(setEnrollment);
  }, []);

  const handleSubmit = () => {
    if (didIt === null || saving) return;
    void hapticService.success();
    setSaving(true);
    void (async () => {
      await completeAssignment(assignmentId, {
        completed: didIt,
        afterMood,
        afterEnergy: null,
        repeatIntent,
        reflection: note || undefined,
      });
      setSaving(false);
      setDone(true);
    })();
  };

  const handleAdvanceProgram = async () => {
    setAdvancing(true);
    setAdvanceNote(null);
    const result = await advanceProgramDay();
    setAdvancing(false);
    if (!result.ok) {
      setAdvanceNote(result.message);
      return;
    }
    void hapticService.success();
    if (result.enrollment) {
      setAdvanceNote(
        t("programs.advanced", { day: result.enrollment.currentDay })
      );
      setEnrollment(result.enrollment);
    } else {
      setAdvanceNote(t("programs.finished"));
      setEnrollment(null);
    }
  };

  if (done) {
    return (
      <div className="flex h-dvh flex-col items-center justify-center overflow-hidden px-6 text-center">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={springs.soft}
          className="w-full max-w-sm space-y-4"
        >
          <div className="text-5xl" aria-hidden>
            ✓
          </div>
          <Text variant="title" as="h1">
            {t("experiment.doneTitle")}
          </Text>
          <Text variant="muted" className="leading-relaxed">
            {t("experiment.doneBody")}
          </Text>

          {enrollment && didIt ? (
            <div className="space-y-2 rounded-2xl border border-border bg-surface-raised p-4 text-left">
              <Text variant="label">{t("experiment.programLabel")}</Text>
              <Text variant="subtle">
                {t("experiment.programAdvanceHint", {
                  day: enrollment.currentDay,
                  total: enrollment.durationDays,
                })}
              </Text>
              <Button
                fullWidth
                variant="soft"
                loading={advancing}
                onClick={() => void handleAdvanceProgram()}
              >
                {t("experiment.markProgramDay")}
              </Button>
              {advanceNote ? (
                <Text variant="caption" className="text-center">
                  {advanceNote}
                </Text>
              ) : null}
            </div>
          ) : null}

          <Button
            fullWidth
            onClick={() => void navigate({ to: "/app/today" })}
          >
            {t("experiment.backToToday")}
          </Button>
          <Button
            fullWidth
            variant="ghost"
            onClick={() => void navigate({ to: "/app/insights" })}
          >
            {t("experiment.seeInsights")}
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="flex h-dvh max-h-dvh flex-col overflow-x-hidden overflow-y-auto overscroll-y-contain bg-background px-5 sm:px-6 pt-[calc(env(safe-area-inset-top)+16px)] pb-[calc(env(safe-area-inset-bottom)+16px)] [-webkit-overflow-scrolling:touch]">
      <button
        type="button"
        onClick={() => void navigate({ to: "/app/today" })}
        className="mb-7 text-sm text-foreground-muted"
      >
        {t("experiment.reflectCancel")}
      </button>

      <Text variant="title" as="h1" className="mb-2">
        {t("experiment.reflectTitle")}
      </Text>
      {experiment ? (
        <Text variant="muted" className="mb-9 leading-relaxed">
          {experiment.title}
        </Text>
      ) : null}

      <div className="space-y-9 pb-4">
        <section className="space-y-4">
          <Text variant="heading" as="h2">
            {t("experiment.didYou")}
          </Text>
          <div className="flex gap-3">
            {[
              { value: true, label: t("experiment.yes") },
              { value: false, label: t("experiment.noPartial") },
            ].map((opt) => (
              <button
                key={String(opt.value)}
                type="button"
                onClick={() => {
                  void hapticService.selection();
                  setDidIt(opt.value);
                }}
                className={cn(
                  "min-h-12 flex-1 rounded-2xl border py-3.5 text-sm font-medium transition-colors",
                  didIt === opt.value
                    ? "border-primary bg-primary-soft text-primary"
                    : "border-border bg-surface-raised text-foreground"
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </section>

        {didIt !== null ? (
          <motion.section
            className="space-y-4"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: durations.normal }}
          >
            <Text variant="heading" as="h2">
              {t("experiment.howNow")}
            </Text>
            <MoodSelector value={afterMood} onChange={setAfterMood} />
          </motion.section>
        ) : null}

        {didIt === true ? (
          <motion.section
            className="space-y-4"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: durations.normal }}
          >
            <Text variant="heading" as="h2">
              {t("experiment.again")}
            </Text>
            <div className="flex gap-2.5">
              {[
                { value: 2 as const, label: t("yes") },
                { value: 1 as const, label: t("maybe") },
                { value: 0 as const, label: t("no") },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    void hapticService.selection();
                    setRepeatIntent(opt.value);
                  }}
                  className={cn(
                    "min-h-12 flex-1 rounded-2xl border py-3 text-sm font-medium transition-colors",
                    repeatIntent === opt.value
                      ? "border-primary bg-primary-soft text-primary"
                      : "border-border bg-surface-raised text-foreground"
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </motion.section>
        ) : null}

        {didIt !== null ? (
          <motion.section
            className="space-y-3"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: durations.normal }}
          >
            <Text variant="heading" as="h2">
              {t("experiment.notice")}{" "}
              <span className="font-normal text-foreground-subtle">
                {t("experiment.optional")}
              </span>
            </Text>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              maxLength={500}
              placeholder={t("experiment.noticePlaceholder")}
              className="min-h-28 w-full resize-none rounded-2xl border border-input bg-surface-raised px-4 py-3.5 text-base leading-relaxed text-foreground shadow-sm placeholder:text-foreground-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            />
          </motion.section>
        ) : null}
      </div>

      <div className="fixed inset-x-0 bottom-0 border-t border-border/80 bg-surface-raised/95 px-5 pt-4 pb-[calc(env(safe-area-inset-bottom)+18px)] backdrop-blur-md">
        <Button
          fullWidth
          disabled={didIt === null}
          loading={saving}
          onClick={handleSubmit}
        >
          {t("experiment.save")}
        </Button>
      </div>
    </div>
  );
}
